import {
  LoginPayload,
  LoginResponseWithConsent,
  LoginResponseWithoutConsent,
  LoginResponsePasswordSetupRequired,
  UserDataResponse,
  VerifyUserPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  FirstFactorAuthResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  LogoutResponse,
} from "../Interfaces/LoginInterface";
import { store } from "../redux/store";
import { apiClient } from "./auth";
import { getOrCreateDeviceId } from "../utils/deviceId";

export type AuthResponse =
  | LoginResponseWithoutConsent
  | LoginResponseWithConsent
  | LoginResponsePasswordSetupRequired;

export const LoginApi = async (
  payload: LoginPayload
): Promise<AuthResponse> => {
  const response = await apiClient.post<LoginPayload, AuthResponse>(
    "/auth/login",
    payload
  );
  return response.data;
};

export const firstFactorAuthApi = async (
  loginToken: string,
  devCode: string,
  deviceId: string = getOrCreateDeviceId(),
  consentDevice: boolean = true
): Promise<FirstFactorAuthResponse> => {
  const response = await apiClient.post<
    VerifyUserPayload,
    FirstFactorAuthResponse
  >("/auth/login/complete-email", {
    loginToken: loginToken,
    code: devCode,
    deviceId: deviceId,
    consentDevice: consentDevice,
  });
  return response.data;
};

export const verifyUserApi = async (
  payload: VerifyUserPayload
): Promise<LoginResponseWithConsent> => {
  const response = await apiClient.post<
    VerifyUserPayload,
    LoginResponseWithConsent
  >("/auth/login/complete-email", payload);
  return response.data;
};

export const userDataApi = async (): Promise<UserDataResponse> => {
  const token = store.getState().auth.accessToken;
  const response = await apiClient.get<UserDataResponse>("/access/me/context", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const forgotPasswordApi = async (
  payload: ForgotPasswordPayload
): Promise<ForgotPasswordResponse> => {
  const response = await apiClient.post<
    ForgotPasswordPayload,
    ForgotPasswordResponse
  >("/auth/password/request", payload);
  return response.data;
};

export const resetPasswordApi = async (
  payload: ResetPasswordPayload
): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<
    ResetPasswordPayload,
    ResetPasswordResponse
  >("/auth/password/setup", payload);
  return response.data;
};

export const resendOtpApi = async (
  loginToken: string
): Promise<{ message: string }> => {
  const response = await apiClient.post<
    { loginToken: string },
    { message: string }
  >("/auth/resend-otp", {
    loginToken: loginToken,
  });
  return response.data;
};

export const logoutApi = async (): Promise<LogoutResponse> => {
  const response = await apiClient.post<{}, LogoutResponse>("/auth/logout", {});
  return response.data;
};

export const refreshSessionApi = async (
  refreshToken: string
): Promise<LoginResponseWithConsent> => {
  const response = await apiClient.post<
    { refreshToken: string },
    LoginResponseWithConsent
  >("/auth/refresh", {
    refreshToken: refreshToken,
  });
  return response.data;
};

/** Self-service profile update payload (PATCH /auth/me). Only representative fields. */
export interface UpdateOwnProfilePayload {
  representative: {
    fullName?: string;
    location?: string;
    hospitalName?: string;
    doctorName?: string;
    unit?: string;
    region?: string;
    teamLeadId?: string;
    departmentId?: string;
  };
}

/**
 * Update own profile (self-service).
 * PATCH /auth/me — any authenticated user can update their representative info.
 */
export const updateOwnProfileApi = async (
  payload: UpdateOwnProfilePayload
): Promise<{ ok: boolean; message: string }> => {
  const response = await apiClient.patch<
    UpdateOwnProfilePayload,
    { ok: boolean; message: string }
  >("/auth/me", payload);
  return response.data;
};
